import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, CheckCheck, CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguage } from '@/contexts/LanguageContext';
import { fmtDate } from '@/lib/api';

// apiClient: either `api` (user) or `adminApi` (staff)
// dataTestPrefix: 'wallet' | 'admin'
export function NotificationsBell({ apiClient, dataTestPrefix = 'wallet' }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/notifications', { params: { limit: 30 } });
      setItems(r.data.items || []);
      setUnread(Number(r.data.unread || 0));
    } catch (err) {
      // Non-critical: user may not be authenticated yet; log for diagnostics only
      if (err?.response?.status && err.response.status !== 401 && err.response.status !== 403) {
        console.warn('[NotificationsBell] load failed:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load();
    pollingRef.current = setInterval(load, 30000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [load]);

  const markRead = async (id) => {
    try {
      await apiClient.post(`/notifications/${id}/read`);
      setItems(list => list.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch (err) {
      console.warn('[NotificationsBell] markRead failed:', err?.message);
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.post('/notifications/read-all');
      setItems(list => list.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (err) {
      console.warn('[NotificationsBell] markAllRead failed:', err?.message);
    }
  };

  const iconFor = (level) => {
    const p = 'h-4 w-4';
    if (level === 'success') return <CircleCheck className={`${p} text-[hsl(152_45%_36%)]`} />;
    if (level === 'warning') return <TriangleAlert className={`${p} text-[hsl(43_84%_38%)]`} />;
    if (level === 'error') return <CircleAlert className={`${p} text-[hsl(0_72%_45%)]`} />;
    return <Info className={`${p} text-[hsl(210_60%_45%)]`} />;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid={`${dataTestPrefix}-notifications-bell`}
          className="relative"
          aria-label={t('notifications.title')}
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid={`${dataTestPrefix}-notifications-unread-badge`}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-navy))] text-[10px] font-semibold flex items-center justify-center border border-white"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 overflow-hidden" data-testid={`${dataTestPrefix}-notifications-drawer`}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-[hsl(var(--brand-navy-2))]">{t('notifications.title')}</div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{unread > 0 ? t('notifications.unread_count', { n: unread }) : t('notifications.all_read')}</div>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              data-testid={`${dataTestPrefix}-notifications-mark-all-read`}
              onClick={markAllRead}
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              {t('notifications.mark_all_read')}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">{t('common.loading')}</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid={`${dataTestPrefix}-notifications-empty`}>
              <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
              {t('notifications.empty')}
            </div>
          )}
          <ul className="divide-y">
            {items.map(n => (
              <li
                key={n.id}
                data-testid={`${dataTestPrefix}-notification-item`}
                className={`px-4 py-3 flex gap-3 transition-colors ${n.is_read ? 'bg-white' : 'bg-[hsl(var(--secondary))]/50'}`}
              >
                <div className="pt-0.5">{iconFor(n.level)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-[hsl(var(--brand-navy-2))] break-words">{n.title}</div>
                    {!n.is_read && (
                      <button
                        type="button"
                        onClick={() => markRead(n.id)}
                        className="shrink-0 text-[10px] uppercase tracking-widest text-[hsl(var(--brand-gold-2))] hover:text-[hsl(var(--brand-navy))] transition-colors"
                        data-testid={`${dataTestPrefix}-notification-mark-read`}
                      >
                        {t('notifications.mark_read')}
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 break-words">{n.body}</div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 tabular-nums">{fmtDate(n.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
